export const SKIN_REVIEW_SLOT_GROUPS = [
  {
    key: 'morning',
    label: 'Morning Routine',
    accent: 'amber',
    slots: [
      { key: 'morning_cleanser', routineKey: 'morning_routine', slotName: 'Cleanser' },
      { key: 'morning_serum', routineKey: 'morning_routine', slotName: 'Serum' },
      { key: 'morning_moisturizer', routineKey: 'morning_routine', slotName: 'Moisturizer' },
      { key: 'morning_sunscreen', routineKey: 'morning_routine', slotName: 'Sunscreen' },
    ],
  },
  {
    key: 'evening',
    label: 'Evening Routine',
    accent: 'violet',
    slots: [
      { key: 'evening_cleanser', routineKey: 'evening_routine', slotName: 'Cleanser' },
      { key: 'evening_serum', routineKey: 'evening_routine', slotName: 'Serum' },
      { key: 'evening_moisturizer', routineKey: 'evening_routine', slotName: 'Moisturizer' },
    ],
  },
];

export const SKIN_REVIEW_SLOT_ORDER = SKIN_REVIEW_SLOT_GROUPS.flatMap((group) =>
  group.slots.map((slot) => slot.key)
);

export const SUMMARY_REVIEW_FIELDS = [
  {
    key: 'model_categories',
    title: 'Model Categories',
    correctionLabel: 'Correct Categories',
    placeholder: 'Write the corrected category text here...',
  },
  {
    key: 'model_analysis',
    title: 'Model Analysis',
    correctionLabel: 'Correct Analysis',
    placeholder: 'Write the corrected analysis here...',
  },
];

export const SUMMARY_REVIEW_FIELD_ORDER = SUMMARY_REVIEW_FIELDS.map((field) => field.key);

export const REVIEW_REASON_OPTIONS = [
  { value: 'wrong_ingredient', label: 'Wrong Ingredient' },
  { value: 'too_generic', label: 'Too Generic' },
  { value: 'wrong_strength', label: 'Wrong Strength' },
  { value: 'wrong_slot', label: 'Wrong Slot' },
  { value: 'wrong_routine_timing', label: 'Wrong Routine Timing' },
  { value: 'missed_skin_type', label: 'Missed Skin Type' },
  { value: 'missed_sensitivity', label: 'Missed Sensitivity' },
  { value: 'safety_issue', label: 'Safety Issue' },
  { value: 'other', label: 'Other' },
];

export const normalizeCorrectionOptionKey = (label) =>
  String(label || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

export const DEFAULT_SKIN_CORRECTION_OPTIONS = [
  'Gentle Cleanser',
  'Salicylic Acid Cleanser',
  'Benzoyl Peroxide Cleanser',
  'Niacinamide Serum',
  'Vitamin C Serum',
  'Azelaic Acid Serum',
  'Retinol Serum',
  'Hyaluronic Acid Serum',
  'Barrier Repair Moisturizer',
  'Lightweight Gel Moisturizer',
  'Rich Moisturizer',
  'Mineral Sunscreen SPF50+',
  'Chemical Sunscreen SPF50+',
  'Oil-Control Sunscreen SPF50+',
].map((label) => ({
  label,
  value: normalizeCorrectionOptionKey(label),
  normalized_key: normalizeCorrectionOptionKey(label),
  scope: 'skin',
  isDefault: true,
}));

const ROUTINE_SLOT_ALIASES = {
  cleanser: 'cleanser',
  facewash: 'cleanser',
  'face wash': 'cleanser',
  serum: 'serum',
  treatment: 'serum',
  treatmentserum: 'serum',
  moisturiser: 'moisturizer',
  moisturizer: 'moisturizer',
  moistizer: 'moisturizer',
  moistorizer: 'moisturizer',
  moisturisercream: 'moisturizer',
  sunscreen: 'sunscreen',
  spf: 'sunscreen',
  sunblock: 'sunscreen',
};

const EMPTY_SLOT_REVIEW = {
  vote: null,
  correction_option_id: null,
  correction_label: null,
  reason_tags: [],
  other_reason_text: null,
  updated_at: null,
  updated_by: null,
};

const EMPTY_FIELD_REVIEW = {
  vote: null,
  correction_text: null,
  updated_at: null,
  updated_by: null,
};

const EMPTY_REVIEW = {
  version: 1,
  slots: {},
  fields: {},
};

const normalizeJsonString = (value) => {
  if (typeof value !== 'string') {
    return null;
  }

  return value
    .trim()
    .replace(/^```json\s*/i, '')
    .replace(/^```\s*/i, '')
    .replace(/\s*```$/i, '');
};

export const parseOutputPayload = (value) => {
  if (!value) {
    return null;
  }

  if (typeof value === 'object') {
    return value;
  }

  if (typeof value !== 'string') {
    return null;
  }

  const normalized = normalizeJsonString(value);
  if (!normalized) {
    return null;
  }

  try {
    return JSON.parse(normalized);
  } catch (_error) {
    return null;
  }
};

const normalizeSlotToken = (value) =>
  String(value || '')
    .toLowerCase()
    .replace(/[^a-z]/g, '');

const getCanonicalSlotName = (value) =>
  ROUTINE_SLOT_ALIASES[normalizeSlotToken(value)] || null;

const getSlotMeta = (slotKey) =>
  SKIN_REVIEW_SLOT_GROUPS.flatMap((group) => group.slots).find((slot) => slot.key === slotKey) || null;

const buildSlotLookup = (parsedOutput, routineKey) => {
  const items = Array.isArray(parsedOutput?.[routineKey]) ? parsedOutput[routineKey] : [];
  const slotLookup = {};

  items.forEach((item) => {
    const canonicalSlotName = getCanonicalSlotName(item?.slot);
    if (!canonicalSlotName) {
      return;
    }

    slotLookup[canonicalSlotName] = {
      slot: item?.slot || null,
      search_query: typeof item?.search_query === 'string' ? item.search_query : '',
      raw: item,
    };
  });

  return slotLookup;
};

export const normalizeSkinRoutineOutput = (value) => {
  const parsed = parseOutputPayload(value);
  const morningLookup = buildSlotLookup(parsed, 'morning_routine');
  const eveningLookup = buildSlotLookup(parsed, 'evening_routine');

  const normalizedSlots = {};
  SKIN_REVIEW_SLOT_GROUPS.forEach((group) => {
    const lookup = group.key === 'morning' ? morningLookup : eveningLookup;
    group.slots.forEach((slot) => {
      const canonicalSlotName = getCanonicalSlotName(slot.slotName);
      const matchedItem = lookup[canonicalSlotName] || null;

      normalizedSlots[slot.key] = {
        key: slot.key,
        routineKey: slot.routineKey,
        slotName: slot.slotName,
        searchQuery: matchedItem?.search_query || '',
        slotLabel: matchedItem?.slot || slot.slotName,
        raw: matchedItem?.raw || null,
      };
    });
  });

  return {
    parsed,
    categories: parsed?.CATEGORIES || '',
    analysis: parsed?.ANALYSIS || '',
    slots: normalizedSlots,
    rawText: typeof value === 'string' ? value : parsed ? JSON.stringify(parsed, null, 2) : '',
  };
};

const normalizeReasonTags = (tags) => {
  if (!Array.isArray(tags)) {
    return [];
  }

  return Array.from(
    new Set(
      tags
        .map((tag) => String(tag || '').trim())
        .filter(Boolean)
        .filter((tag) => REVIEW_REASON_OPTIONS.some((option) => option.value === tag))
    )
  );
};

const normalizeSlotReview = (value) => {
  const slotReview = value && typeof value === 'object' ? value : {};
  const vote = slotReview.vote === 'up' || slotReview.vote === 'down' ? slotReview.vote : null;

  return {
    vote,
    correction_option_id: slotReview.correction_option_id || null,
    correction_label:
      typeof slotReview.correction_label === 'string' && slotReview.correction_label.trim()
        ? slotReview.correction_label.trim()
        : null,
    reason_tags: normalizeReasonTags(slotReview.reason_tags),
    other_reason_text:
      typeof slotReview.other_reason_text === 'string' && slotReview.other_reason_text.trim()
        ? slotReview.other_reason_text.trim()
        : null,
    updated_at: slotReview.updated_at || null,
    updated_by: slotReview.updated_by || null,
  };
};

const normalizeFieldReview = (value) => {
  const fieldReview = value && typeof value === 'object' ? value : {};
  const vote = fieldReview.vote === 'up' || fieldReview.vote === 'down' ? fieldReview.vote : null;

  return {
    vote,
    correction_text:
      typeof fieldReview.correction_text === 'string' && fieldReview.correction_text.trim()
        ? fieldReview.correction_text.trim()
        : null,
    updated_at: fieldReview.updated_at || null,
    updated_by: fieldReview.updated_by || null,
  };
};

export const normalizeModelOutputReview = (value) => {
  const parsed = parseOutputPayload(value);
  const review = parsed && typeof parsed === 'object' ? parsed : EMPTY_REVIEW;
  const slots = {};
  const fields = {};

  SKIN_REVIEW_SLOT_ORDER.forEach((slotKey) => {
    slots[slotKey] = normalizeSlotReview(review?.slots?.[slotKey]);
  });

  SUMMARY_REVIEW_FIELD_ORDER.forEach((fieldKey) => {
    fields[fieldKey] = normalizeFieldReview(review?.fields?.[fieldKey]);
  });

  return {
    version: 1,
    slots,
    fields,
  };
};

export const getDefaultSlotReview = () => ({
  ...EMPTY_SLOT_REVIEW,
  reason_tags: [],
});

export const getSlotReview = (review, slotKey) => {
  const normalizedReview = normalizeModelOutputReview(review);
  return normalizedReview.slots[slotKey] || getDefaultSlotReview();
};

export const getDefaultFieldReview = () => ({
  ...EMPTY_FIELD_REVIEW,
});

export const getFieldReview = (review, fieldKey) => {
  const normalizedReview = normalizeModelOutputReview(review);
  return normalizedReview.fields[fieldKey] || getDefaultFieldReview();
};

const getReviewTargetBucket = (reviewKey) => (
  SKIN_REVIEW_SLOT_ORDER.includes(reviewKey) ? 'slots' : 'fields'
);

const normalizeReviewEntry = (reviewKey, value) => (
  getReviewTargetBucket(reviewKey) === 'slots' ? normalizeSlotReview(value) : normalizeFieldReview(value)
);

const getExistingReviewEntry = (normalizedReview, reviewKey) => (
  getReviewTargetBucket(reviewKey) === 'slots'
    ? normalizedReview.slots[reviewKey] || getDefaultSlotReview()
    : normalizedReview.fields[reviewKey] || getDefaultFieldReview()
);

export const buildNextModelOutputReview = (
  currentReview,
  reviewKey,
  reviewPatch,
  metadata = {}
) => {
  const normalizedReview = normalizeModelOutputReview(currentReview);
  const reviewBucket = getReviewTargetBucket(reviewKey);
  const currentEntry = getExistingReviewEntry(normalizedReview, reviewKey);
  const nextEntry = normalizeReviewEntry(reviewKey, {
    ...currentEntry,
    ...reviewPatch,
    updated_at: metadata.updatedAt || new Date().toISOString(),
    updated_by: metadata.updatedBy || currentEntry.updated_by || null,
  });

  return {
    version: 1,
    slots: {
      ...normalizedReview.slots,
      ...(reviewBucket === 'slots' ? { [reviewKey]: nextEntry } : {}),
    },
    fields: {
      ...normalizedReview.fields,
      ...(reviewBucket === 'fields' ? { [reviewKey]: nextEntry } : {}),
    },
  };
};

export const getReviewSaveBadge = (slotSaveStates) => {
  const states = Object.values(slotSaveStates || {});
  if (states.some((state) => state === 'saving')) {
    return { label: 'Saving', tone: 'amber' };
  }

  if (states.some((state) => state === 'error')) {
    return { label: 'Save issue', tone: 'rose' };
  }

  if (states.some((state) => state === 'saved')) {
    return { label: 'Saved', tone: 'emerald' };
  }

  return { label: 'Ready', tone: 'slate' };
};

export const buildReviewCorrectionOption = (option) => {
  if (!option) {
    return null;
  }

  if (typeof option === 'string') {
    const normalizedKey = normalizeCorrectionOptionKey(option);
    return {
      label: option,
      value: normalizedKey,
      normalized_key: normalizedKey,
    };
  }

  const label = String(option.label || option.correction_label || '').trim();
  const normalizedKey = option.normalized_key || normalizeCorrectionOptionKey(label);

  return {
    id: option.id || null,
    label,
    value: option.id || normalizedKey,
    normalized_key: normalizedKey,
    scope: option.scope || 'skin',
    is_active: option.is_active !== false,
    isDefault: Boolean(option.isDefault),
  };
};

export const mergeCorrectionOptions = (...optionSets) => {
  const merged = new Map();

  optionSets.flat().forEach((option) => {
    const normalizedOption = buildReviewCorrectionOption(option);
    if (!normalizedOption?.label) {
      return;
    }

    const key = normalizedOption.normalized_key || normalizeCorrectionOptionKey(normalizedOption.label);
    const existing = merged.get(key);
    merged.set(key, {
      ...existing,
      ...normalizedOption,
      value: normalizedOption.id || normalizedOption.value || key,
    });
  });

  return Array.from(merged.values()).sort((left, right) => left.label.localeCompare(right.label));
};

export const getCorrectionOptionForReview = (options, slotReview) => {
  const review = slotReview || getDefaultSlotReview();
  const optionList = Array.isArray(options) ? options : [];

  if (review.correction_option_id) {
    const matchedById = optionList.find((option) => option.id === review.correction_option_id);
    if (matchedById) {
      return matchedById;
    }
  }

  if (review.correction_label) {
    const normalizedKey = normalizeCorrectionOptionKey(review.correction_label);
    const matchedByLabel = optionList.find(
      (option) =>
        option.normalized_key === normalizedKey ||
        option.label.toLowerCase() === review.correction_label.toLowerCase()
    );

    if (matchedByLabel) {
      return matchedByLabel;
    }

    return buildReviewCorrectionOption(review.correction_label);
  }

  return null;
};

export const getSlotGroupAccentClasses = (accent) => {
  if (accent === 'amber') {
    return {
      panel: 'border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50',
      chip: 'bg-amber-100 text-amber-800',
      slotLabel: 'text-amber-700',
    };
  }

  return {
    panel: 'border-violet-200 bg-gradient-to-br from-violet-50 to-fuchsia-50',
    chip: 'bg-violet-100 text-violet-800',
    slotLabel: 'text-violet-700',
  };
};

export const getSlotMetaByKey = (slotKey) => getSlotMeta(slotKey);
